import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import { State } from 'src/state/ducks';
import { ShopEntity } from 'src/firebase/db/Shops';
import { ShopBagEntity, shopBagInitialEntity } from 'src/firebase/db/ShopBags';

import Button from 'src/components/atoms/button/Button';
import SingleInputFormBlock from 'src/components/molecules/form/SingleInputFormBlock';

type Props = {
  shop: ShopEntity;
  errors: State['shop']['bags']['validationErrors'];
  onSave: (query: ShopBagEntity[]) => void;
  onConfirmDelete: (onDelete: Function) => Promise<void>;
};

const mapEntityToState = (shop: ShopEntity) => {
  const { bags } = shop;
  const values = bags && bags.length ? bags : [shopBagInitialEntity];

  return {
    bags: values.map((v) => ({
      ...v,
      price: v.price ? v.price : '',
    })),
  };
};

const mapStateToEntity = (state: ReturnType<typeof mapEntityToState>): ShopBagEntity[] =>
  state.bags.map((v) => ({
    ...v,
    price: typeof v.price === 'number' ? v.price : 0,
  }));

const ShopBagsForm = (props: Props) => {
  const { shop, errors, onSave, onConfirmDelete } = props;
  const [state, setState] = useState(mapEntityToState(shop));

  const { bags } = state;

  useEffect(() => {
    setState(mapEntityToState(shop));
  }, [shop]);

  const onChangeName = useCallback((index: number, name: string) => {
    setState((prev) => {
      const bags = prev.bags.map((bag, i) => ({
        ...bag,
        name: i === index ? name : bag.name,
      }));
      return { bags };
    });
  }, []);

  const onChangePrice = useCallback((index: number, v: string) => {
    const value = parseInt(v);
    const price = isNaN(value) ? '' : value;
    setState((prev) => {
      const bags = prev.bags.map((bag, i) => ({
        ...bag,
        price: i === index ? price : bag.price,
      }));
      return { bags };
    });
  }, []);

  const onClickAddBag = useCallback(
    () =>
      setState((prev) => {
        const { bags } = prev;
        bags.push(shopBagInitialEntity);
        return { bags };
      }),
    [],
  );

  // 保存
  const wrapOnSave = useCallback(() => {
    onSave(mapStateToEntity(state));
  }, [onSave, state]);

  // 削除
  const onDelete = useCallback(
    (index: number) => {
      const remove = () =>
        setState((prev) => ({
          bags: prev.bags.filter((v, i) => i !== index),
        }));
      const bag = bags.find((v, i) => i === index);
      if (bag && (bag.name || bag.price)) {
        onConfirmDelete(remove);
      } else {
        remove();
      }
    },
    [bags, onConfirmDelete],
  );

  return (
    <Form>
      {bags.map((bag, i) => (
        <div className="bag" key={`ShopBagsForm|${i}`}>
          <div className="items">
            <div className="item">
              <SingleInputFormBlock
                isRequired
                labelName="レジ袋サイズ"
                inputType="text"
                inputValue={bag.name}
                onChange={(name) => onChangeName(i, name)}
                placeholder="サイズ（S・M・L）"
                isError={!!errors[`name_${i}`]}
              />
            </div>
            <div className="item">
              <SingleInputFormBlock
                isRequired
                labelName="レジ袋価格(税抜)"
                inputType="number"
                inputValue={bag.price}
                onChange={(price) => onChangePrice(i, price)}
                placeholder="価格を入力してください"
                isError={!!errors[`price_${i}`]}
                min={0}
              />
            </div>
          </div>
          <div className="delete">
            <button type="button" onClick={() => onDelete(i)}>
              削除する
            </button>
          </div>
        </div>
      ))}
      <div className="add">
        <Button
          type="button"
          isDisabled={false}
          clickEvent={onClickAddBag}
          text="他のサイズを追加する"
        />
      </div>
      <div className="submit">
        <div className="submit-item">
          <Button type="button" isDisabled={false} clickEvent={wrapOnSave} text="保存する" />
        </div>
      </div>
    </Form>
  );
};

const Form = styled.form`
  .bag {
    &:first-child {
      .delete {
        display: none;
      }
    }

    .items {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 20px;

      .item {
        width: 49%;
      }
    }

    .delete {
      margin-top: 8px;
      text-align: right;
      &:first-child {
        display: none;
      }

      button {
        font-size: 12px;
        color: #ff0303;
        text-decoration: underline;
      }
    }
  }

  .add {
    margin-top: 24px;
    margin-bottom: 48px;
    button {
      font-size: 12px;
      width: 160px;
      margin: 0 auto;
      display: block;
    }
  }
`;

export default ShopBagsForm;
